import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Tooltip,
  CircularProgress,
  Chip
} from '@mui/material';
import { useData } from '../../context/DataContext';
import { useNavigate } from 'react-router-dom';
import {
  Article as PostsIcon,
  Work as JobsIcon,
  People as DoctorsIcon,
  Business as DepartmentsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as BoardIcon,
  PeopleAlt as TeamIcon,
  Description as ApplicationsIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import apiClient from '../../services/api';

const AdminDashboard = () => {
  const { state } = useData();
  const { posts, jobs, doctors, departments, pages, jobApplications, teamMembers } = state;
  const navigate = useNavigate();
  
  // Get user permissions from localStorage
  const [userPermissions, setUserPermissions] = useState(null);
  
  useEffect(() => {
    const perms = localStorage.getItem('currentUserPermissions');
    if (perms) {
      setUserPermissions(JSON.parse(perms));
    }
  }, []);

  // System status state
  const [systemStatus, setSystemStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Function to fetch system status
  const fetchSystemStatus = async () => {
    setStatusLoading(true);
    try {
      const response = await apiClient.getSystemStatus();
      if (response.success) {
        setSystemStatus(response.data);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Failed to fetch system status:', error);
      // Set fallback status if API fails
      setSystemStatus({
        website: { status: 'online', message: 'Website operational' },
        database: { status: 'error', message: 'Connection failed' },
        adminPanel: { status: 'active', message: 'Admin panel active' },
        overall: { status: 'degraded', message: 'Some systems experiencing issues' }
      });
    } finally {
      setStatusLoading(false);
    }
  };

  // Load system status on component mount
  useEffect(() => {
    fetchSystemStatus();
    const interval = setInterval(fetchSystemStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  // Helper function to get status icon and color
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'online':
      case 'connected':
      case 'active':
      case 'operational':
        return { icon: <CheckCircleIcon />, color: 'success.main', text: '●' };
      case 'degraded':
      case 'warning':
        return { icon: <WarningIcon />, color: 'warning.main', text: '●' };
      case 'offline':
      case 'disconnected':
      case 'error':
      case 'inactive':
        return { icon: <ErrorIcon />, color: 'error.main', text: '●' };
      default:
        return { icon: <WarningIcon />, color: 'grey.500', text: '●' };
    }
  };

  const stats = [
    {
      title: 'Total Posts',
      value: posts.length,
      icon: <PostsIcon />,
      color: '#1976d2',
      path: '/admin/posts',
      perm: 'can_manage_posts'
    },
    {
      title: 'Active Jobs',
      value: jobs.filter(job => job.isActive).length,
      icon: <JobsIcon />,
      color: '#2e7d32',
      path: '/admin/jobs',
      perm: 'can_manage_jobs'
    },
    {
      title: 'Doctors',
      value: doctors.length,
      icon: <DoctorsIcon />,
      color: '#ed6c02',
      path: '/admin/doctors',
      perm: 'can_manage_doctors'
    },
    {
      title: 'Departments',
      value: departments.length,
      icon: <DepartmentsIcon />,
      color: '#dc2626',
      path: '/admin/departments',
      perm: 'can_manage_departments'
    },
    {
      title: 'Team Members',
      value: teamMembers?.length || 0,
      icon: <TeamIcon />,
      color: '#1565c0',
      path: '/admin/team-members',
      perm: 'can_manage_team'
    },
    {
      title: 'Job Applications',
      value: jobApplications.length,
      icon: <ApplicationsIcon />,
      color: '#ff6b35',
      path: '/admin/job-applications',
      perm: 'can_view_job_applications'
    }
  ];

  // Filter stats based on user permissions
  const filteredStats = userPermissions ? stats.filter(stat => !stat.perm || userPermissions[stat.perm]) : [];

  const recentPosts = posts.slice(0, 3);
  const recentJobs = jobs.slice(0, 3);

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Dashboard Overview
      </Typography>
      
      {/* Statistics Cards */}
      {filteredStats.length > 0 ? (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {filteredStats.map((stat) => (
            <Grid item xs={12} sm={6} md={3} key={stat.title}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { transform: 'translateY(-4px)', transition: 'transform 0.3s ease' }
                }}
                onClick={() => navigate(stat.path)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ color: stat.color, mr: 2 }}>
                      {stat.icon}
                    </Box>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                      {stat.value}
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="text.secondary">
                    {stat.title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'grey.50', borderRadius: 2, mb: 4 }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No Access Granted
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You don't have permission to access any admin sections.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please contact your administrator to request access.
          </Typography>
        </Box>
      )}

      {/* Quick Actions */}
      {filteredStats.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Grid container spacing={2}>
                  {userPermissions?.can_manage_posts && (
                    <Grid item xs={12} sm={6}>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        fullWidth
                        onClick={() => navigate('/admin/posts')}
                        sx={{ mb: 1 }}
                      >
                        Add Post
                      </Button>
                    </Grid>
                  )}
                  {userPermissions?.can_manage_jobs && (
                    <Grid item xs={12} sm={6}>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        fullWidth
                        onClick={() => navigate('/admin/jobs')}
                        sx={{ mb: 1 }}
                      >
                        Add Job
                      </Button>
                    </Grid>
                  )}
                  {userPermissions?.can_manage_doctors && (
                    <Grid item xs={12} sm={6}>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        fullWidth
                        onClick={() => navigate('/admin/doctors')}
                        sx={{ mb: 1 }}
                      >
                        Add Doctor
                      </Button>
                    </Grid>
                  )}
                  {userPermissions?.can_manage_board && (
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        startIcon={<BoardIcon />}
                        fullWidth
                        onClick={() => navigate('/admin/board-members')}
                        sx={{ mb: 1 }}
                      >
                        Manage Board Members
                      </Button>
                    </Grid>
                  )}
                  {userPermissions?.can_manage_team && (
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        startIcon={<TeamIcon />}
                        fullWidth
                        onClick={() => navigate('/admin/team-members')}
                        sx={{ mb: 1 }}
                      >
                        Manage Team Members
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Activity
                </Typography>
                <List dense>
                  {userPermissions?.can_manage_posts && (
                    <ListItem>
                      <ListItemIcon>
                        <PostsIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${posts.length} posts published`}
                        secondary="Latest: New COVID-19 Safety Protocols"
                      />
                    </ListItem>
                  )}
                  {userPermissions?.can_manage_jobs && (
                    <ListItem>
                      <ListItemIcon>
                        <JobsIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${jobs.filter(job => job.isActive).length} active job postings`}
                        secondary="Latest: Registered Nurse - Emergency Department"
                      />
                    </ListItem>
                  )}
                  {userPermissions?.can_manage_doctors && (
                    <ListItem>
                      <ListItemIcon>
                        <DoctorsIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${doctors.length} doctors on staff`}
                        secondary="Latest: Dr. Emily Rodriguez added"
                      />
                    </ListItem>
                  )}
                  {filteredStats.length === 0 && (
                    <ListItem>
                      <ListItemIcon>
                        <WarningIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary="No permissions granted"
                        secondary="Contact administrator for access"
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Recent Content */}
      {filteredStats.length > 0 && (
        <Grid container spacing={3}>
          {/* Recent Posts */}
          {userPermissions?.can_manage_posts && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Recent Posts
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => navigate('/admin/posts')}
                    >
                      View All
                    </Button>
                  </Box>
                  <List dense>
                    {recentPosts.map((post) => (
                      <ListItem key={post.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={post.title}
                          secondary={`${post.author} • ${new Date(post.date).toLocaleDateString()}`}
                        />
                        <Box>
                          <Button size="small" startIcon={<EditIcon />}>
                            Edit
                          </Button>
                          <Button size="small" startIcon={<DeleteIcon />} color="error">
                            Delete
                          </Button>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Recent Jobs */}
          {userPermissions?.can_manage_jobs && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Recent Job Postings
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => navigate('/admin/jobs')}
                    >
                      View All
                    </Button>
                  </Box>
                  <List dense>
                    {recentJobs.map((job) => (
                      <ListItem key={job.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={job.title}
                          secondary={`${job.department} • ${job.type} • ${new Date(job.postedDate).toLocaleDateString()}`}
                        />
                        <Box>
                          <Button size="small" startIcon={<EditIcon />}>
                            Edit
                          </Button>
                          <Button size="small" startIcon={<DeleteIcon />} color="error">
                            Delete
                          </Button>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* System Status */}
      {userPermissions?.can_manage_settings && (
        <Paper sx={{ p: 3, mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              System Status
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {lastUpdated && (
                <Typography variant="caption" color="text.secondary">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </Typography>
              )}
              <Button
                size="small"
                startIcon={statusLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={fetchSystemStatus}
                disabled={statusLoading}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {statusLoading && !systemStatus ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Website Status */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Tooltip title={systemStatus?.website?.message || 'Website status'}>
                    <Box>
                      <Typography 
                        variant="h4" 
                        color={getStatusDisplay(systemStatus?.website?.status).color}
                        sx={{ mb: 1 }}
                      >
                        {getStatusDisplay(systemStatus?.website?.status).text}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        Website Online
                      </Typography>
                      {systemStatus?.website?.uptime && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Uptime: {Math.floor(systemStatus.website.uptime / 60)}m
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                </Card>
              </Grid>

              {/* Database Status */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Tooltip title={systemStatus?.database?.message || 'Database status'}>
                    <Box>
                      <Typography 
                        variant="h4" 
                        color={getStatusDisplay(systemStatus?.database?.status).color}
                        sx={{ mb: 1 }}
                      >
                        {getStatusDisplay(systemStatus?.database?.status).text}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        Database Connected
                      </Typography>
                      {systemStatus?.database?.doctorCount !== undefined && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {systemStatus.database.doctorCount} doctors
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                </Card>
              </Grid>

              {/* Admin Panel Status */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Tooltip title={systemStatus?.adminPanel?.message || 'Admin panel status'}>
                    <Box>
                      <Typography 
                        variant="h4" 
                        color={getStatusDisplay(systemStatus?.adminPanel?.status).color}
                        sx={{ mb: 1 }}
                      >
                        {getStatusDisplay(systemStatus?.adminPanel?.status).text}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        Admin Panel Active
                      </Typography>
                      {systemStatus?.adminPanel?.features && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {systemStatus.adminPanel.features.length} features
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                </Card>
              </Grid>

              {/* Overall System Status */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Tooltip title={systemStatus?.overall?.message || 'Overall system status'}>
                    <Box>
                      <Typography 
                        variant="h4" 
                        color={getStatusDisplay(systemStatus?.overall?.status).color}
                        sx={{ mb: 1 }}
                      >
                        {getStatusDisplay(systemStatus?.overall?.status).text}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {systemStatus?.overall?.status === 'operational' ? 'All Systems Normal' : 'System Status'}
                      </Typography>
                      <Chip 
                        label={systemStatus?.overall?.status?.toUpperCase() || 'UNKNOWN'} 
                        size="small" 
                        color={
                          systemStatus?.overall?.status === 'operational' ? 'success' : 
                          systemStatus?.overall?.status === 'degraded' ? 'warning' : 'error'
                        }
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Tooltip>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Server Information */}
          {systemStatus?.server && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Server Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Memory Usage: {systemStatus.server.memory.used}MB / {systemStatus.server.memory.total}MB
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Uptime: {Math.floor(systemStatus.server.uptime / 3600)}h {Math.floor((systemStatus.server.uptime % 3600) / 60)}m
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Node.js: {systemStatus.server.version}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default AdminDashboard; 